/**
 * Public author-name display — shared by every surface that shows ANOTHER
 * user's name (comments, community feed, …). Two rules, both privacy-driven:
 *
 *  1. Strip values that must never be public — migrated WordPress usernames of
 *     the shape `patreon_<patreonId>` (leaking the person's Patreon id) and
 *     email addresses.
 *  2. Show the FIRST NAME only. A full "First Last" byline exposes someone's
 *     real legal name, which is itself PII — so only the leading token renders
 *     (this also matches how the legacy WP site displayed comment authors).
 *
 * Keep public author names going through `publicFirstName` so a person's
 * Patreon id, email, or last name can never surface in a public byline — even
 * if a bad `display_name` row slips through (remediation is separate).
 */

const LEAKY_PUBLIC_NAME = /^patreon_\d+$/i

/** Trimmed value, or null when it's leaky (patreon_<id> / email) or empty. */
export function sanitizePublicName(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim()
  if (!t) return null
  if (LEAKY_PUBLIC_NAME.test(t) || t.includes('@')) return null
  return t
}

/**
 * The public author name to render: first name only, leaky values stripped.
 * Pass the live profile `display_name` first, then any denormalized fallback
 * (e.g. a comment's stored `author_name` for guests / deleted users).
 */
export function publicFirstName(
  displayName: string | null | undefined,
  fallbackName?: string | null | undefined,
  fallback = 'Member',
): string {
  const name = sanitizePublicName(displayName) ?? sanitizePublicName(fallbackName) ?? fallback
  return name.split(/\s+/)[0]
}
